import React from "react";
import { Clock, Check, AlertCircle, RefreshCw, Sparkles, XCircle, Download } from "lucide-react";
import SignatureCentre from "@/components/SignatureCentre";
import { downloadAcceptanceLetter } from "@/lib/acceptanceLetter";

const STATUS_META = {
  submitted: {
    icon: Clock,
    title: "Submitted · Awaiting Review",
    blurb: "Thank you. Your application is in the queue. We aim to review every submission within 7 business days. We may email you with follow-up questions before deciding.",
    color: "var(--bloom-ink)",
  },
  under_review: {
    icon: RefreshCw,
    title: "Under Review",
    blurb: "Our team is currently reading your application carefully. Expect to hear from us soon.",
    color: "var(--bloom-warning)",
  },
  more_info_requested: {
    icon: AlertCircle,
    title: "More Information Requested",
    blurb: "We need a few more details before we can decide. Please re-open your application and address the notes below.",
    color: "var(--bloom-warning)",
  },
  approved: {
    icon: Sparkles,
    title: "Welcome, Founding Grower",
    blurb: "Congratulations. Your application has been accepted. Below you will find the documents to sign in order to formally activate your place in the 2026 / 2027 cohort.",
    color: "var(--bloom-brand)",
  },
  active: {
    icon: Sparkles,
    title: "Founding Grower · Activated",
    blurb: "Every document is signed. You are formally part of the inaugural Heritage Bloom Club. Your onboarding will open shortly.",
    color: "var(--bloom-brand)",
  },
  rejected: {
    icon: XCircle,
    title: "Not Selected For This Cohort",
    blurb: "Thank you for the time you took on this application. We were unable to offer a spot in this Founding Grower cohort. The notes below explain our reasoning.",
    color: "var(--bloom-accent)",
  },
};

export default function ApplicationStatus({ application, signatureStatus, onSigned, onReopen }) {
  const meta = STATUS_META[application.status] || STATUS_META.submitted;
  const Icon = meta.icon;
  const s = application.score;
  const isApproved = application.status === "approved" || application.status === "active";

  const handleDownloadLetter = () => {
    downloadAcceptanceLetter({
      name: application.data?.full_legal_name,
      farmName: application.data?.farm_name,
      region: application.data?.region,
      score: application.score,
      decidedAt: application.decision_at,
      activatedAt: application.activated_at,
      notes: application.review_notes,
      signatureName: application.data?.full_legal_name,
    });
  };

  return (
    <div className="space-y-8" data-testid="application-status">
      <div className="bloom-card p-10 md:p-14 text-center" data-testid="status-card">
        <Icon className="mx-auto mb-4" style={{ color: meta.color }} strokeWidth={1.5} size={36} />
        <h2 className="font-serif text-3xl md:text-4xl mb-3">{meta.title}</h2>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] max-w-xl mx-auto mb-6">{meta.blurb}</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-xl mx-auto mt-8 text-left">
          <Tile label="Submitted" value={application.submitted_at ? new Date(application.submitted_at).toLocaleDateString() : "·"} />
          <Tile label="Status" value={application.status.replace(/_/g, " ")} />
          <Tile label="Score" value={s ? `${s.score}/100 · ${s.tier}` : "·"} accent={s && s.score >= 75} />
        </div>

        {application.status === "more_info_requested" && (
          <button onClick={onReopen} className="bloom-btn bloom-btn-primary mt-8" data-testid="reopen-application-btn">
            Reopen & Edit Application
          </button>
        )}

        {isApproved && (
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={handleDownloadLetter} className="bloom-btn bloom-btn-outline" data-testid="download-acceptance-letter-btn">
              <Download size={14} /> Download Acceptance Letter
            </button>
          </div>
        )}
      </div>

      {application.review_notes && (
        <div className="bloom-card p-8" data-testid="reviewer-notes">
          <div className="bloom-label mb-3">Notes from the review team</div>
          <p className="font-serif italic text-lg leading-relaxed">{application.review_notes}</p>
        </div>
      )}

      {isApproved && (
        <SignatureCentre
          application={application}
          signatureStatus={signatureStatus}
          onSigned={onSigned}
        />
      )}
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="border-l border-[var(--bloom-border)] pl-3">
      <div className="bloom-label mb-1">{label}</div>
      <div className={`font-serif text-lg ${accent ? "text-[var(--bloom-brand)]" : ""}`} style={{ textTransform: "capitalize" }}>{value}</div>
    </div>
  );
}
